export const COOKIE_CONSENT_STORAGE_KEY = "xarith_cookie_consent";
export const COOKIE_CONSENT_EVENT = "xarith:cookie-consent-changed";

export type ConsentState = {
  analytics: boolean;
  marketing: boolean;
};

type PostHogClient = Awaited<typeof import("posthog-js")>["default"];

let posthogClientPromise: Promise<PostHogClient | null> | null = null;

async function loadPostHogClient(): Promise<PostHogClient | null> {
  if (typeof window === "undefined") return null;

  if (!posthogClientPromise) {
    posthogClientPromise = import("posthog-js")
      .then((module) => module.default)
      .catch(() => null);
  }

  return posthogClientPromise;
}

export function loadConsentState(): ConsentState | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentState;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  return loadConsentState()?.analytics === true;
}

export async function ensurePostHogInitialized(): Promise<PostHogClient | null> {
  const posthog = await loadPostHogClient();
  if (!posthog) return null;
  if (posthog.__loaded) return posthog;

  const key = (import.meta.env.VITE_POSTHOG_KEY as string | undefined) || "phc_rVUIu7jUULE8t1CMlI6WbMUKtPf6KdJJykpXp4tT3k8";
  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://eu.i.posthog.com";
  if (!key || !host) return null;

  posthog.init(key, {
    api_host: host,
    defaults: "2026-01-30",
  });

  return posthog;
}

export function saveConsentState(state: ConsentState) {
  localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent<ConsentState>(COOKIE_CONSENT_EVENT, { detail: state }));

  if (state.analytics) {
    void ensurePostHogInitialized().then((posthog) => {
      posthog?.opt_in_capturing();
    });
    return;
  }

  if (!posthogClientPromise) return;
  void posthogClientPromise.then((posthog) => {
    if (posthog?.__loaded) {
      posthog.opt_out_capturing();
    }
  });
}

export function capturePostHog(event: string, properties?: Record<string, unknown>) {
  if (!hasAnalyticsConsent()) return Promise.resolve();
  return ensurePostHogInitialized().then((posthog) => {
    posthog?.capture(event, properties);
  });
}

// Captures without consent gate — for first-party product events (signups, identifies)
// where the data belongs to us, not third-party tracking.
export function capturePostHogFirstParty(event: string, properties?: Record<string, unknown>) {
  return ensurePostHogInitialized().then((posthog) => {
    posthog?.capture(event, properties);
  });
}

export function identifyPostHog(distinctId: string, properties?: Record<string, unknown>) {
  return ensurePostHogInitialized().then((posthog) => {
    posthog?.identify(distinctId, properties);
  });
}

export function resetPostHog() {
  if (!posthogClientPromise) return Promise.resolve();
  return posthogClientPromise.then((posthog) => {
    if (posthog?.__loaded) {
      posthog.reset(true);
    }
  });
}
